import ProductLink from '@components/common/ProductLink';
import ErrorModal from '@components/error/ErrorModal';
import ProductActionNotifyMe from '@moncler/components/product/ProductActionNotifyMe';
import ProductSelectionSizes from '@moncler/components/product/ProductSelectionSizes';
import Button from '@ui/button/Button';
import analytics from '@utils/analytics';
import { getColorVariations, getSizeVariations } from '@utils/product';
import useBreakpoint from '@utils/useBreakpoint';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@common/utils/api';
import DwMarker from '@common/components/common/DwMarker';
import ProductAddToBag from '@moncler/components/product/ProductActionAddToBag';
import ProductImage from '@moncler/components/product/ProductImage';
import ProductPrice from '@moncler/components/product/ProductPrice';
// CUSTOM TILE VARIATION FOR LOOKS
const ShopTheLookTile = (props) => {
    const { t } = useTranslation();
    const tileEl = useRef(null);
    const productObject = useMemo(() => {
        return typeof props.product === 'string' ? JSON.parse(props.product) : props.product;
    }, [props.product]);
    const [product, setProduct] = useState();
    const [currentSize, setCurrentSize] = useState();
    const [currentColor, setCurrentColor] = useState();
    const [variantPrice, setVariantPrice] = useState();
    const [showSelectSizeMsg, setShowSelectSizeMsg] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [showOverlay, setShowOverlay] = useState(false);
    const onOpenModal = () => {
        setShowOverlay(true);
    };
    const onCloseModal = () => {
        document.body.classList.remove('page-scrolled');
        setShowOverlay(false);
    };
    const [isDesktop, setIsDesktop] = useState(false);
    const currentBreakpoint = useBreakpoint();
    // gestisco hide/show quick buy su mobile
    useEffect(() => {
        if (currentBreakpoint >= window.mqObj.md) {
            setIsDesktop(true);
        }
        else {
            setIsDesktop(false);
        }
    }, [currentBreakpoint]);
    // gestisco l'assegnazione di product al caricamento
    useEffect(() => {
        setProduct(productObject);
        const colorVariations = getColorVariations(productObject);
        // per questo tipo di tile è supportata solo una variante colore - no color change
        const selectedColor = colorVariations.find((v) => v.selected);
        setCurrentColor(selectedColor ? selectedColor : colorVariations[0]);
    }, [props.product]);
    const sizeVariations = useMemo(() => {
        let sizeVariations = getSizeVariations(product);
        // se è taglia unica la imposto
        if (sizeVariations.length == 1) {
            setCurrentSize(sizeVariations[0]);
        }
        return sizeVariations;
    }, [product]);
    const changeSize = (variation) => {
        var _a;
        setCurrentSize(variation);
        if (props.isTechnicalPage) {
            analytics.TPChangeSizeColor(`grenoble-${product.masterId}`, `size-${variation.id}`);
        }
        else {
            analytics.productImpressionsChange(product, props.index, undefined, variation.id);
        }
        // qui non va cambiato il prodotto, va solo salvata la taglia selezionata che serve per l'add to cart
        if (((_a = product === null || product === void 0 ? void 0 : product.price) === null || _a === void 0 ? void 0 : _a.type) === 'range') {
            // prezzo diverso per taglia
            api.variantPrice(`${product.masterId}${currentColor.id}${variation.id}`).then((data) => setVariantPrice(data));
        }
    };
    const quickBuyButton = useMemo(() => {
        if (!currentSize && (currentColor === null || currentColor === void 0 ? void 0 : currentColor.selectable)) {
            return (React.createElement(Button, { type: "button", cls: [], onClick: () => {
                    //indico all'utente di selezionare la taglia
                    if (!showSelectSizeMsg) {
                        setShowSelectSizeMsg(true);
                    }
                } }, t('button.addtobag')));
        }
        else if (currentSize === null || currentSize === void 0 ? void 0 : currentSize.selectable) {
            setShowSelectSizeMsg(false);
            return (React.createElement(React.Fragment, null,
                showOverlay && (React.createElement("span", { className: "visually-hidden", role: "alert" }, t('bag.justadded'))),
                React.createElement(ProductAddToBag, { product: product, size: currentSize, noModifiers: true, onAddStart: onOpenModal, callback: onCloseModal, pushAnalytics: () => {
                        if (props.isTechnicalPage) {
                            analytics.AddToBagTP(product, `${props.region.toLowerCase()}_grenoble_tp_${product.analyticsAttributes.dimension38.toLowerCase()}`, currentSize.value);
                        }
                        else {
                            analytics.quickShopAddToBag(product.masterId, 'LOOKPRODUCT');
                            analytics.quickShopAddToBagDetails(product, currentSize.value);
                        }
                    }, inShopTheLook: true, updateErrorMessage: (msg) => setErrorMessage(msg) })));
        }
        else {
            return (React.createElement(ProductActionNotifyMe, { product: product, color: currentColor, size: currentSize, onOpenModal: onOpenModal, onCloseModal: onCloseModal, from: "plp", secondary: true, triggerOpenSize: () => {
                    //indico all'utente di selezionare la taglia
                    if (!showSelectSizeMsg) {
                        setShowSelectSizeMsg(true);
                    }
                } }));
        }
    }, [currentSize, currentColor, showSelectSizeMsg, product]);
    return (React.createElement("div", { ref: tileEl, "data-id": `${currentColor ? currentColor.productId : ''}${currentSize ? currentSize.id : ''}`, "data-product": product === null || product === void 0 ? void 0 : product.id, className: classNames('product-tile', 'in-look') }, product && (React.createElement(React.Fragment, null,
        React.createElement(DwMarker, { type: "product", uuid: product.uuid }),
        React.createElement("div", { className: "product-tile__card" },
            React.createElement(ProductLink, { index: props.index, product: product, linktype: 'PLP', pageType: props.pageType, className: "product-tile__link", ariaLabel: t('label.goToProductName', { productName: product.productName, color: '' }), recommender: props === null || props === void 0 ? void 0 : props.recommender, isTechnicalPage: props.isTechnicalPage, region: props === null || props === void 0 ? void 0 : props.region, analyticsSuffix: props === null || props === void 0 ? void 0 : props.analyticsSuffix },
                React.createElement("div", { className: "product-tile__image-wrapper" }, props.customImage ? (React.createElement("img", { alt: product.productName, src: props.customImage, className: "custom_image" })) : (React.createElement(ProductImage, { productId: product.id, img: product.imgs, tiletype: 'LOOKPRODUCT', type: 'X', className: "product-tile__image" }))))),
        React.createElement("div", { className: classNames('product-tile__info', 'lookinfo') },
            (props.headingLevel === 'h2' && React.createElement("h2", { className: "product-tile__name" }, product.productName)) || React.createElement("h3", { className: "product-tile__name" }, product.productName),
            React.createElement("span", { className: "product-tile__price" },
                React.createElement(ProductPrice, { price: variantPrice || (product === null || product === void 0 ? void 0 : product.price) })),
            !window.unshoppable && isDesktop && (React.createElement(React.Fragment, null,
                React.createElement(ProductSelectionSizes, { variationValues: sizeVariations, currentId: currentSize === null || currentSize === void 0 ? void 0 : currentSize.id, onChangeVariation: (sizeVariation) => {
                        changeSize(sizeVariation);
                    }, error: showSelectSizeMsg && t('label.pleaseselectasize') }),
                quickBuyButton)),
            props.showMobileButton && !isDesktop && (React.createElement("a", { href: product.productUrl, className: "shop-now button button--secondary" }, t('button.shopnow')))),
        React.createElement(ErrorModal, { message: errorMessage, onClose: () => setErrorMessage(undefined) })))));
};
export default ShopTheLookTile;
